import React, { useEffect, useState, useCallback } from "react";
import { getConsent, hasDecided, saveConsent } from "../lib/consent";
import { loadFbPixel } from "../lib/pixelLoader";

/**
 * AEPD-compliant cookie banner.
 *
 * - Shows on first visit (or when consent has expired > 12 months).
 * - Two equal-weight primary buttons: "Aceptar" and "Rechazar".
 * - "Configurar" opens a panel with Analíticas + Publicitarias, both OFF by
 *   default, and the "Necesarias" category always ON (fixed).
 * - When advertising is granted the Meta Pixel script is loaded (fbevents.js
 *   is injected + fbq('init', ID)); when analytics is granted PostHog init()
 *   is invoked. NEITHER of these run before consent.
 * - Choice is stored 12 months. "Configurar cookies" footer link reopens
 *   the panel via the global 'sircs-cookie-settings-open' event.
 */
export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [mode, setMode] = useState("banner"); // "banner" | "settings"
  const [prefs, setPrefs] = useState({ analytics: false, advertising: false });

  // On mount: show if the user hasn't decided yet.
  useEffect(() => {
    if (!hasDecided()) {
      setMode("banner");
      setVisible(true);
    } else {
      // Re-apply the stored decision so scripts load on refresh.
      const c = getConsent();
      if (c) applyConsent(c);
    }

    const openHandler = () => {
      const stored = getConsent();
      setPrefs({
        analytics: !!(stored && stored.analytics),
        advertising: !!(stored && stored.advertising),
      });
      setMode("settings");
      setVisible(true);
    };
    window.addEventListener("sircs-cookie-settings-open", openHandler);
    return () => window.removeEventListener("sircs-cookie-settings-open", openHandler);
  }, []);

  const applyConsent = useCallback((state) => {
    if (state.advertising) {
      try {
        loadFbPixel();
      } catch (_) {
        /* ignore */
      }
    }
    if (state.analytics && typeof window.__initPostHog === "function") {
      try {
        window.__initPostHog();
      } catch (_) {
        /* ignore */
      }
    }
  }, []);

  const commit = useCallback(
    (state) => {
      const saved = saveConsent(state);
      applyConsent(saved);
      setVisible(false);
    },
    [applyConsent]
  );

  const acceptAll = () => commit({ analytics: true, advertising: true });
  const rejectAll = () => commit({ analytics: false, advertising: false });
  const saveCustom = () => commit(prefs);

  if (!visible) return null;

  return (
    <div
      className="cookie-banner-overlay"
      role="dialog"
      aria-modal="false"
      aria-live="polite"
      aria-labelledby="cookie-banner-title"
      data-testid="cookie-banner"
    >
      <div className="cookie-banner-panel">
        {mode === "banner" ? (
          <>
            <p
              className="cookie-banner-title"
              id="cookie-banner-title"
              data-testid="cookie-banner-title"
            >
              Cookies en esta web
            </p>
            <p className="cookie-banner-body">
              Usamos cookies necesarias para que la web funcione. Con tu permiso, también
              utilizamos cookies analíticas y publicitarias (píxel de Meta) para medir y
              mejorar la web y las campañas. Puedes aceptarlas, rechazarlas o configurarlas.
              Más información en la{" "}
              <a href="/politica-de-cookies">Política de Cookies</a>.
            </p>
            <div className="cookie-banner-actions">
              <button
                type="button"
                className="cookie-btn cookie-btn-reject"
                onClick={rejectAll}
                data-testid="cookie-reject"
              >
                Rechazar
              </button>
              <button
                type="button"
                className="cookie-btn cookie-btn-accept"
                onClick={acceptAll}
                data-testid="cookie-accept"
              >
                Aceptar
              </button>
              <button
                type="button"
                className="cookie-btn cookie-btn-configure"
                onClick={() => setMode("settings")}
                data-testid="cookie-configure"
              >
                Configurar
              </button>
            </div>
          </>
        ) : (
          <>
            <p
              className="cookie-banner-title"
              id="cookie-banner-title"
              data-testid="cookie-settings-title"
            >
              Configurar cookies
            </p>
            <p className="cookie-banner-body">
              Elige qué categorías quieres permitir. Puedes cambiar tu decisión en cualquier
              momento desde “Configurar cookies” en el pie de página.
            </p>
            <div className="cookie-settings-list">
              <div className="cookie-settings-row">
                <div>
                  <h4>Necesarias</h4>
                  <p>
                    Imprescindibles para que la web funcione y para recordar tu elección de
                    cookies. Siempre activas.
                  </p>
                </div>
                <button
                  type="button"
                  className="cookie-switch"
                  data-on="true"
                  data-fixed="true"
                  aria-label="Cookies necesarias, siempre activas"
                  disabled
                />
              </div>

              <div className="cookie-settings-row">
                <div>
                  <h4>Analíticas</h4>
                  <p>
                    Nos permiten saber cuántas personas visitan la web y qué páginas leen.
                  </p>
                </div>
                <button
                  type="button"
                  className="cookie-switch"
                  data-on={prefs.analytics ? "true" : "false"}
                  aria-pressed={prefs.analytics}
                  aria-label="Activar cookies analíticas"
                  onClick={() => setPrefs((p) => ({ ...p, analytics: !p.analytics }))}
                  data-testid="cookie-toggle-analytics"
                />
              </div>

              <div className="cookie-settings-row">
                <div>
                  <h4>Publicitarias</h4>
                  <p>
                    Píxel de Meta: mide la eficacia de los anuncios y ayuda a mostrarte
                    contenidos relevantes en Instagram y Facebook.
                  </p>
                </div>
                <button
                  type="button"
                  className="cookie-switch"
                  data-on={prefs.advertising ? "true" : "false"}
                  aria-pressed={prefs.advertising}
                  aria-label="Activar cookies publicitarias"
                  onClick={() =>
                    setPrefs((p) => ({ ...p, advertising: !p.advertising }))
                  }
                  data-testid="cookie-toggle-advertising"
                />
              </div>
            </div>
            <div className="cookie-settings-footer">
              <button
                type="button"
                className="cookie-btn cookie-btn-reject"
                onClick={rejectAll}
                data-testid="cookie-settings-reject"
              >
                Rechazar todo
              </button>
              <button
                type="button"
                className="cookie-btn cookie-btn-accept"
                onClick={saveCustom}
                data-testid="cookie-settings-save"
              >
                Guardar preferencias
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
